import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { toast } from 'react-hot-toast';
import { sanitizeLojaDto, lojaUpdatePayload } from '@/services/configDto';

export interface Loja {
  id: string;
  tenant_id?: string | null;
  slug: string;
  nome: string;
  endereco?: string | null;
  telefone?: string | null;
  email?: string | null;
  ativa: boolean;
  assinatura_atendente_ativa?: boolean;
  config?: any;
  has_gemini_key?: boolean;
  has_uazapi_token?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface LojasState {
  lojas: Loja[];
  loading: boolean;
  fetched: boolean;
  fetchLojas: (force?: boolean) => Promise<void>;
  getById: (id: string | null | undefined) => Loja | undefined;
  getBySlug: (slug: string | null | undefined) => Loja | undefined;
  createLoja: (data: { nome: string; slug?: string; endereco?: string; telefone?: string; email?: string; assinatura_atendente_ativa?: boolean }) => Promise<boolean>;
  updateLoja: (id: string, updates: Partial<Loja>) => Promise<boolean>;
  deleteLoja: (id: string) => Promise<boolean>;
}

export const useLojasStore = create<LojasState>()(
  persist(
    (set, get) => ({
      lojas: [],
      loading: false,
      fetched: false,

      fetchLojas: async (force = false) => {
        if (get().fetched && !force) return;
        set({ loading: true });

        try {
          const { fetchWithAuth } = await import('@/services/api');
          const res = await fetchWithAuth('/api/lojas');
          if (res.ok) {
            const data = await res.json();
            set({ lojas: (Array.isArray(data) ? data : []).map(sanitizeLojaDto) as Loja[], fetched: true });
          } else {
            console.error('[LOJAS_STORE] Error fetching crm_lojas via API:', res.statusText);
          }
        } catch (err) {
          console.error('[LOJAS_STORE] Exception:', err);
        } finally {
          set({ loading: false, fetched: true });
        }
      },

      getById: (id) => {
        if (!id) return undefined;
        return get().lojas.find(l => l.id === id);
      },

      getBySlug: (slug) => {
        if (!slug) return undefined;
        return get().lojas.find(l => l.slug === slug);
      },

      createLoja: async (data) => {
        try {
          const { fetchWithAuth } = await import('@/services/api');
          const res = await fetchWithAuth('/api/lojas', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          if (res.ok) {
            await get().fetchLojas(true);
            toast.success('Loja cadastrada com sucesso!');
            return true;
          }
          const errData = await res.json().catch(() => ({}));
          toast.error(errData.error || 'Erro ao cadastrar loja.');
        } catch (err: any) {
          console.error('[LOJAS_STORE] createLoja API error:', err);
          toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
        }
        return false;
      },

      updateLoja: async (id, updates) => {
        try {
          const { fetchWithAuth } = await import('@/services/api');
          const res = await fetchWithAuth(`/api/lojas/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(lojaUpdatePayload(updates))
          });
          if (res.ok) {
            await get().fetchLojas(true);
            toast.success('Loja atualizada com sucesso!');
            return true;
          }
          const errData = await res.json().catch(() => ({}));
          toast.error(errData.error || 'Erro ao atualizar loja.');
        } catch (err: any) {
          console.error('[LOJAS_STORE] updateLoja API error:', err);
          toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
        }
        return false;
      },

      deleteLoja: async (id) => {
        try {
          const { fetchWithAuth } = await import('@/services/api');
          const res = await fetchWithAuth(`/api/lojas/${id}`, {
            method: 'DELETE'
          });
          if (res.ok) {
            await get().fetchLojas(true);
            toast.success('Loja excluída com sucesso!');
            return true;
          }
          const errData = await res.json().catch(() => ({}));
          toast.error(errData.error || 'Erro ao excluir loja.');
        } catch (err: any) {
          console.error('[LOJAS_STORE] deleteLoja API error:', err);
          toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
        }
        return false;
      }
    }),
    {
      name: 'task-crm-lojas-cache',
      version: 1,
      migrate: (persisted) => ({
        lojas: Array.isArray((persisted as Partial<LojasState>)?.lojas)
          ? (persisted as Partial<LojasState>).lojas!.map(sanitizeLojaDto) : []
      }),
      partialize: (state) => ({
        lojas: state.lojas.map(sanitizeLojaDto)
      }),
      merge: (persisted, current) => ({
        ...current,
        lojas: Array.isArray((persisted as Partial<LojasState>)?.lojas)
          ? (persisted as Partial<LojasState>).lojas!.map(sanitizeLojaDto) : []
      })
    }
  )
);
